import { ChildProfile, Story, SubscriptionTier } from '../types';

const STORY_CONTINUATIONS: Record<ChildProfile['language'], string> = {
  de: 'Das Abenteuer geht weiter.', en: 'The adventure continues.', fr: 'L’aventure continue.', es: 'La aventura continúa.', zh: '冒险继续。', ja: '冒険は続きます。', it: 'L’avventura continua.', ar: 'تستمر المغامرة.', hi: 'साहसिक यात्रा आगे बढ़ती है।',
};

const CONTINUATION_BRIDGES: Record<ChildProfile['language'], string> = {
  de: 'Webe die Geschichte als direktes neues Kapitel weiter. Knüpfe ohne Wiederholung an das Ende an, behalte Figuren, Stimmung und die bereits gelöste Aufgabe im Gedächtnis und beginne sofort mit einer neuen, altersgerechten Wendung.',
  en: 'Continue the story as a direct new chapter. Pick up from its ending without repetition, keep the characters, mood and resolved challenge in mind, and begin immediately with a fresh age-appropriate turn.',
  fr: 'Poursuis l’histoire comme un nouveau chapitre direct. Reprends juste après la fin sans répétition, garde les personnages, l’ambiance et le défi déjà résolu, puis commence aussitôt un nouveau rebondissement adapté à l’âge.',
  es: 'Continúa la historia como un nuevo capítulo directo. Retoma justo después del final sin repetir, conserva los personajes, el ambiente y el reto ya resuelto, y empieza enseguida un nuevo giro apropiado para la edad.',
  zh: '把故事作为紧接着的新章节继续编织。不要重复前文，要记住人物、氛围和已经解决的挑战，并立刻开始一个适龄的新转折。',
  ja: '物語を直後から続く新しい章として紡いでください。繰り返さず、登場人物、雰囲気、解決した課題を保ちながら、年齢に合った新しい展開をすぐに始めてください。',
  it: 'Continua la storia come un nuovo capitolo diretto. Riprendi subito dopo il finale senza ripetizioni, mantieni personaggi, atmosfera e sfida già risolta e avvia immediatamente una nuova svolta adatta all’età.',
  ar: 'تابع القصة كفصل جديد مباشر. ابدأ من نهايتها بلا تكرار، واحفظ الشخصيات والأجواء والتحدي الذي حُلّ، ثم ابدأ فورًا منعطفًا جديدًا مناسبًا للعمر.',
  hi: 'कहानी को पिछले अंत के ठीक बाद एक नए अध्याय की तरह आगे बुनें। दोहराव न करें, पात्रों, माहौल और हल हो चुकी चुनौती को याद रखें और तुरंत उम्र के अनुकूल नया मोड़ शुरू करें।',
};

const RANDOM_FALLBACKS: Record<ChildProfile['language'], string> = {
  de: 'entdeckt eine neue Traumwelt.', en: 'discovers a new dream world.', fr: 'découvre un nouveau monde de rêve.', es: 'descubre un nuevo mundo de sueños.', zh: '发现了一个新的梦幻世界。', ja: '新しい夢の世界を見つけます。', it: 'scopre un nuovo mondo dei sogni.', ar: 'يكتشف عالم أحلام جديدًا.', hi: 'एक नई सपनों की दुनिया खोजता है।',
};

function ageGroup(age: number) {
  if (age <= 5) return '3-5';
  if (age <= 8) return '6-8';
  return '9-11';
}

function dataUrlToBlob(dataUrl: string) {
  const [header, data] = dataUrl.split(',');
  const mimeType = header.match(/^data:([^;]+);base64$/)?.[1] || 'application/octet-stream';
  const binary = atob(data || '');
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType });
}

const TEMPORARY_SERVICE_PATTERN = /\b(429|500|502|503|504)\b|unavailable|high demand|overload|temporar|rate limit|resource_exhausted|quota/i;

function getErrorText(payload: any) {
  const raw = payload?.error?.message || payload?.error || payload?.message || '';
  if (typeof raw !== 'string') return String(raw || '');
  try {
    const nested = JSON.parse(raw);
    return String(nested?.error?.message || nested?.message || raw);
  } catch {
    return raw;
  }
}

function friendlyRequestError(payload: any, status: number) {
  const detail = getErrorText(payload);
  if (status >= 500 || TEMPORARY_SERVICE_PATTERN.test(detail)) {
    return 'Die Traumwerkstatt ist gerade sehr gefragt. Wir versuchen es kurz noch einmal – falls es nicht klappt, tippe bitte gleich erneut auf Starten.';
  }
  return detail || 'Die Anfrage ist fehlgeschlagen.';
}

async function requestWithQuickRetry(input: RequestInfo | URL, init: RequestInit, retries = 1) {
  let response = await fetch(input, init);
  for (let attempt = 0; attempt < retries && [429, 500, 502, 503, 504].includes(response.status); attempt += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, 650 * (attempt + 1)));
    response = await fetch(input, init);
  }
  return response;
}

async function responsePayload(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(friendlyRequestError(payload, response.status)) as Error & { status?: number; payload?: any };
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

// Audio is personal to the current family, so this cache lives only in the
// current browser tab. It prevents a page or a preloaded next page from asking
// the voice service twice, without storing narration on a shared server cache.
export type NarrationAudio = {
  audio: string;
  encoding?: 'pcm_s16le' | 'mp3';
  sampleRate?: number;
};

const narrationCache = new Map<string, Promise<NarrationAudio | undefined>>();
const MAX_NARRATION_CACHE_ENTRIES = 12;

function narrationCacheKey(text: string, voice?: string, language?: string) {
  return `${voice || 'Kind'}:${language || 'de'}:${text}`;
}

function rememberNarration(key: string, request: Promise<NarrationAudio | undefined>) {
  narrationCache.set(key, request);
  if (narrationCache.size > MAX_NARRATION_CACHE_ENTRIES) {
    const oldestKey = narrationCache.keys().next().value;
    if (oldestKey) narrationCache.delete(oldestKey);
  }
  request.catch(() => narrationCache.delete(key));
  return request;
}

export const isContentSafe = async (prompt: string, imageBase64?: string): Promise<boolean> => {
  try {
    if (imageBase64) {
      const body = new FormData();
      body.append('photo', dataUrlToBlob(imageBase64), 'referenzfoto.jpg');
      const response = await fetch('/api/safety-check-photo', { method: 'POST', credentials: 'same-origin', body });
      const payload = await responsePayload(response);
      return payload.safe === true;
    }
    const response = await fetch('/api/safety-check', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt }),
    });
    const payload = await responsePayload(response);
    return payload.safe === true;
  } catch (error) {
    console.error('Safety check client error:', error);
    throw error;
  }
};

export const generateRandomPrompt = async (profile: ChildProfile): Promise<string> => {
  // A saved photo reference is a large data URL on this profile. A random
  // idea only needs the child's name and language, so never send the profile
  // object itself (and therefore never send a photo) for this tiny request.
  const promptProfile = {
    name: profile.name,
    language: profile.language,
  };
  const response = await requestWithQuickRetry('/api/random-prompt', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profile: promptProfile }),
  });
  const payload = await responsePayload(response);
  return payload.prompt || `${profile.name} ${RANDOM_FALLBACKS[profile.language]}`;
};

export const generateStoryBook = async (
  profile: ChildProfile,
  prompt: string,
  _tier: SubscriptionTier,
  previousStory?: Story,
): Promise<Story> => {
  const body = new FormData();
  const previousEnding = previousStory?.pages?.at(-1)?.content?.replace(/\s+/g, ' ').slice(-650) || '';
  const requestedTurn = (prompt || STORY_CONTINUATIONS[profile.language]).trim().slice(0, 300);
  const idea = previousStory
    ? `${CONTINUATION_BRIDGES[profile.language]}\nTitel der bisherigen Geschichte: ${previousStory.title}\nLetzte Szene: ${previousEnding}\nWunsch für das neue Kapitel: ${requestedTurn}`.slice(0, 1200)
    : prompt;
  body.append('payload', JSON.stringify({
    childName: profile.name,
    ageGroup: ageGroup(profile.age),
    idea,
    favoriteCharacter: '',
    world: 'Magische Traumwelt',
    mood: 'warm, spannend und hoffnungsvoll',
    language: profile.language,
    photoConsent: Boolean(profile.photoUri),
    continuationOf: previousStory?.id || undefined,
  }));
  if (profile.photoUri) body.append('childPhoto', dataUrlToBlob(profile.photoUri), 'kind-referenzfoto.jpg');

  const response = await requestWithQuickRetry('/api/stories', {
    method: 'POST',
    credentials: 'same-origin',
    body,
  });
  const payload = await responsePayload(response);
  const raw = payload.story;
  return {
    id: raw.id,
    title: raw.title,
    pages: (raw.scenes || []).map((scene: any, index: number) => ({
      pageNumber: index + 1,
      content: scene.text,
      illustrationPrompt: scene.imagePrompt,
      imageUrl: raw.images?.[index]?.url || '',
      imagePending: raw.images?.[index]?.pending === true,
      imageFailed: raw.images?.[index]?.failed === true,
      imageErrorCode: raw.images?.[index]?.errorCode || undefined,
    })),
    createdAt: raw.createdAt,
    isSaved: true,
    isSafe: true,
    authorId: payload.user?.uid || 'current-user',
    isPaid: true,
    audioUrl: raw.audioUrl || null,
    demo: Boolean(raw.demo),
    language: profile.language,
  };
};

export const generatePageSpeech = async (text?: string, voice?: string, language?: string): Promise<NarrationAudio | undefined> => {
  if (!text) return undefined;
  const key = narrationCacheKey(text, voice, language);
  const cached = narrationCache.get(key);
  if (cached) return cached;
  return rememberNarration(key, (async () => {
    const response = await fetch('/api/narration', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, voice, language }),
    });
    const payload = await responsePayload(response);
    return payload.audio ? { audio: payload.audio, encoding: payload.encoding, sampleRate: payload.sampleRate } : undefined;
  })());
};

export const prefetchPageSpeech = (text?: string, voice?: string, language?: string) => {
  if (!text) return;
  void generatePageSpeech(text, voice, language).catch(() => undefined);
};
